import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';
import { FaMoneyBillAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import { Form, Card, Row, Col, InputGroup } from 'react-bootstrap';
import * as Yup from 'yup';
import CurrencyInput from 'react-currency-input-field';

import useBalance from 'hooks/useBalance';

import { StoreState } from 'store/createStore';

import api from 'services/api';
import swal from 'services/swal';
import history from 'services/history';
import toast from 'services/toast';

import PageTitle from 'components/PageTitle';
import FormWrapper from 'components/FormWrapper';
import FormHeader from 'components/FormHeader';
import PanelButton from 'components/PanelButton';
import Error from 'components/Error';

import { Container } from './styles';

interface RequestWithdrawFormValues {
  valor: string;
}

const New: React.FC = () => {
  const { t } = useTranslation();

  const client = useSelector((state: StoreState) => state.client.info);
  const sites = useSelector((state: StoreState) => state.sites.list);

  const { balance, refetch, error: balanceError } = useBalance(
    client.idCliente,
    client.tipoCobranca,
    sites.length > 0 ? sites[0].idSite : undefined,
  );

  const submitForm = useCallback(
    async (values: RequestWithdrawFormValues, formikHelpers) => {
      const valor = parseFloat(
        values.valor.replace(/\./, '').replace(/,/, '.'),
      );
      try {
        await api.post(
          `clientes/v1/representante/${client.idRepresentante}/saque`,
          {
            valor,
          },
        );

        formikHelpers.resetForm();
        refetch();

        toast.fire(t('pages:withdrawNew.withdrawSuccess'));

        history.push('/financeiro/saques');
      } catch (err) {
        swal.fire({
          title: t('pages:withdrawNew.withdrawError'),
          html: err.response.data.error_description,
        });
      }
    },
    [client, t, refetch],
  );

  if (balanceError) {
    return <Error />;
  }

  return (
    <Container>
      <PageTitle
        title={t('titles:financial.title')}
        description={t('titles:financial.description')}
        icon={<FaMoneyBillAlt color="#FFFFFF" size={24} />}
      />
      <FormWrapper>
        <FormHeader
          title={t('pages:withdrawNew.title')}
          description={t('pages:withdrawNew.description')}
          helpContent={
            <div>
              <p>{t('pages:withdraw.helpContent.p1')}</p>
              <p>
                <Trans i18nKey="pages:withdraw.helpContent.p2">
                  p2
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://painel4.hostnet.com.br/helpdesk"
                  >
                    access
                  </a>
                </Trans>
              </p>
              <p>{t('pages:withdraw.helpContent.p3')}</p>
            </div>
          }
        />

        <Formik
          enableReinitialize
          validateOnMount
          initialValues={{
            valor: `${balance.balance}`,
          }}
          validationSchema={Yup.object().shape({
            valor: Yup.string().test(
              'valor',
              t('pages:withdrawNew.invalidValue'),
              (value: string | undefined) => {
                if (!value) {
                  return false;
                }
                const valor: number = parseFloat(
                  value.replace(/\./, '').replace(/,/, '.'),
                );
                if (valor > balance.balance || valor <= 0) {
                  return false;
                }
                return true;
              },
            ),
          })}
          onSubmit={submitForm}
        >
          {props => {
            function handleOnValueChange(value: string | undefined): void {
              let valor = '';
              if (value !== '' && value !== undefined) {
                valor = value.search(/^[.,]/) === 0 ? `0${value}` : value;
              }
              props.setFieldValue('valor', valor);
            }
            return (
              <Form onSubmit={props.handleSubmit}>
                <Card.Body className="fieldset">
                  <Form.Group as={Row}>
                    <Form.Label column sm={2}>
                      {t('pages:withdrawNew.balance')}
                    </Form.Label>
                    <Col sm={10}>
                      <Form.Control
                        plaintext
                        readOnly
                        value={balance.formattedBalance}
                      />
                      <Form.Text className="text-muted">
                        {t('pages:withdrawNew.balanceDescription')}
                      </Form.Text>
                    </Col>
                  </Form.Group>
                  {balance.balance > 0 && (
                    <Form.Group as={Row}>
                      <Form.Label column sm={2}>
                        {t('pages:withdrawNew.value')}
                      </Form.Label>
                      <Col sm={10}>
                        <InputGroup className="mr-sm-2" size="sm">
                          <InputGroup.Prepend>
                            <InputGroup.Text>R$</InputGroup.Text>
                          </InputGroup.Prepend>
                          <CurrencyInput
                            intlConfig={{ locale: 'pt-br' }}
                            id="value"
                            className="form-control"
                            name="value"
                            placeholder="0,00"
                            disabled={props.isSubmitting}
                            onValueChange={handleOnValueChange}
                            decimalsLimit={2}
                            decimalScale={2}
                            maxLength={12}
                            allowNegativeValue={false}
                            value={props.values.valor}
                          />
                        </InputGroup>
                        {props.errors.valor && (
                          <Form.Control.Feedback
                            type="invalid"
                            className="d-block"
                          >
                            {props.errors.valor}
                          </Form.Control.Feedback>
                        )}
                      </Col>
                    </Form.Group>
                  )}
                </Card.Body>
                <div className="border-top pt-2 pb-2 pl-3">
                  {balance.balance > 0 && (
                    <PanelButton
                      type="submit"
                      className="mr-1"
                      disabled={props.isSubmitting || !props.isValid}
                    >
                      {props.isSubmitting
                        ? t('common:sending')
                        : t('common:send')}
                    </PanelButton>
                  )}

                  <PanelButton
                    variant="secondary"
                    forwardedAs={Link}
                    to="/financeiro/saques"
                    disabled={props.isSubmitting}
                  >
                    {t('common:back')}
                  </PanelButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      </FormWrapper>
    </Container>
  );
};

export default New;
